import * as THREE from 'three';
import { GLTF, GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { disposeMaterialTextures } from '../utils/disposeSceneResources';
import { loadWithCdnFallback } from '../../utils/loadWithCdnFallback';
import { fetchWithTtfbTimeout } from '../../utils/fetchWithTtfbTimeout';
import { readResponseBytesWithProgress } from '../../utils/readResponseBytes';
import vertexShader from './shaders/preview-points.vert';
import fragmentShader from './shaders/preview-points.frag';

interface PreviewModelOptions {
    scene: THREE.Scene;
    options: {
        url: string;
        transform?: Partial<{ position: THREE.Vector3, rotation: THREE.Euler, scale: THREE.Vector3 }>;
        onLoadProgress?: (event: { loadedBytes: number, totalBytes: number }) => void;
        style: {
            pointSizeFactor?: number;     // Default: 1.0
            appearanceDuration?: number;  // Duration of point cloud appearance, default: 2.0
            highPriorityAppearanceRadius?: number; // Points inside sphere ot that radius will appear with higher priority, default: 1.0
            backgroundColor?: string;     // hex color, default: '#000000'
            wavesCenter?: { x: number; y: number; z: number }; // Wave origin in world coordinates
            wavesMinDistance?: number;    // Minimum distance from center where waves start (in world units), default: 0
            wavesAmplitude?: number;      // 1-10, default: 3.0 - wave width
            wavesPeriod?: number;         // 1-10, default: 1.0 - number of simultaneous waves
            wavesSpeed?: number;          // Speed multiplier
            wavesColor?: string;          // hex color, default: '#ffffff'
            wavesColorIntensity?: number;  // 0-10, default: 5.0
            wavesDisplacementAxis?: 'x' | 'y' | 'z'; // default: 'y'
            wavesDisplacement?: number;    // Displacement as percentage of model size (0.01 = 1%), default: 0.01
            revealDistanceScale?: number;  // Scale factor for reveal distance calculation, default: 2.5
            revealUse3d?: boolean;         // Use 3D distance for reveal instead of XZ plane, default: false
            colorMultiplier?: number;      // Multiplies vertex colors before wave blend, default: 1.0
            sizeAttenuation?: boolean;     // If true, point pixel size scales inversely with view depth (closer = bigger). Default: false (constant pixel size, prior behavior).
            attenuationScale?: number;     // Multiplier when sizeAttenuation is on: gl_PointSize = pointSize * attenuationScale / -viewZ. Higher = bigger points. Default: 5.0.
        }
    };
}

const DEFAULT_STYLE: Required<PreviewModelOptions['options']['style']> = {
    pointSizeFactor: 1.0,
    appearanceDuration: 3,
    highPriorityAppearanceRadius: 0.4,
    backgroundColor: '#000000',
    wavesCenter: { x: 0, y: 0, z: 0 },
    wavesMinDistance: 0,
    wavesAmplitude: 3.0,
    wavesPeriod: 1.0,
    wavesSpeed: 1.0,
    wavesColor: '#ffffff',
    wavesColorIntensity: 5.0,
    wavesDisplacementAxis: 'y',
    wavesDisplacement: 0.01,
    revealDistanceScale: 2.5,
    revealUse3d: false,
    colorMultiplier: 1.0,
    sizeAttenuation: false,
    attenuationScale: 5.0,
};

export interface WavesConfig {
    wavesCenter?: { x: number; y: number; z: number };
    wavesMinDistance?: number;
    wavesAmplitude?: number;
    wavesPeriod?: number;
    wavesSpeed?: number;
    wavesColorIntensity?: number;
    wavesDisplacement?: number;
}

export class PreviewModel {
    private scene: THREE.Scene;
    private transform: PreviewModelOptions['options']['transform'];
    private revealOrigin: THREE.Vector3;
    private disposed = false;
    private gltfLoader: GLTFLoader;
    private gltf: GLTF | null = null;
    private style: Required<PreviewModelOptions['options']['style']>;
    private maxDistance = 10.0; // Store for runtime updates

    public loadPromise: Promise<void>;

    constructor({ scene, options }: PreviewModelOptions) {
        this.scene = scene;
        this.transform = options.transform;
        this.revealOrigin = new THREE.Vector3(0, 0, 0);

        // Initialize spherical waves parameters with defaults
        this.style = {
            ...DEFAULT_STYLE,
            ...options.style,
        };

        this.gltfLoader = new GLTFLoader();
        this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);

        this.loadPromise = loadWithCdnFallback(
            options.url,
            async (url) => {
                const response = await fetchWithTtfbTimeout(url);
                if (!response.ok) {
                    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
                }
                const bytes = await readResponseBytesWithProgress(response, (loaded, total) => {
                    options.onLoadProgress?.({
                        loadedBytes: loaded,
                        totalBytes: total ?? 0,
                    });
                });
                return new Promise<GLTF>((resolve, reject) => {
                    this.gltfLoader.parse(bytes.buffer as ArrayBuffer, '', resolve, reject);
                });
            },
            { assetType: 'preview-glb' },
        ).then((gltf) => {
            this.gltf = gltf;
            this.initializeModel();
        });
    }

    public setRevealOrigin(revealOrigin: THREE.Vector3): void {
        this.revealOrigin = revealOrigin.clone();

    }

    public update(params: { time: number; splatRevealTime?: number; }): void {
        if (!this.gltf) {
            return;
        }

        let foundPointsObjects = 0;
        this.gltf.scene.traverseVisible((child) => {
            if (child instanceof THREE.Points && child.material instanceof THREE.ShaderMaterial) {
                foundPointsObjects++;
                const uniforms = child.material.uniforms;

                if (uniforms.uRevealOrigin) {
                    uniforms.uRevealOrigin.value.set(
                        this.revealOrigin.x,
                        this.revealOrigin.y,
                        this.revealOrigin.z
                    );
                }

                // Update global effect time (for waves/noise)
                if (uniforms.uTime) {
                    uniforms.uTime.value = params.time;
                }

                // Update splat reveal time (for hiding points overlapping with splats)
                if (params.splatRevealTime !== undefined && uniforms.uRevealTime) {
                    uniforms.uRevealTime.value = params.splatRevealTime;
                }
            }
        });
    }

    public needsRerender(): boolean {
        return this.gltf !== null && !this.disposed;
    }

    public updateWavesConfig(config: WavesConfig): void {
        if (!this.gltf) {
            return;
        }

        this.gltf.scene.traverse((child) => {
            if (child instanceof THREE.Points && child.material instanceof THREE.ShaderMaterial) {
                const uniforms = child.material.uniforms;

                // Update wave center (convert world coordinates to local)
                if (config.wavesCenter !== undefined && uniforms.uCenter) {
                    const worldCenter = new THREE.Vector3(
                        config.wavesCenter.x,
                        config.wavesCenter.y,
                        config.wavesCenter.z
                    );
                    // Transform world -> local using inverse of the object's world matrix
                    const inverseMatrix = child.matrixWorld.clone().invert();
                    const localCenter = worldCenter.applyMatrix4(inverseMatrix);
                    uniforms.uCenter.value.copy(localCenter);
                }

                if (config.wavesAmplitude !== undefined && uniforms.uWavesAmplitude) {
                    uniforms.uWavesAmplitude.value = config.wavesAmplitude;
                }

                if (config.wavesPeriod !== undefined && uniforms.uWavePeriod) {
                    uniforms.uWavePeriod.value = config.wavesPeriod;
                }

                if (config.wavesSpeed !== undefined && uniforms.uWavesSpeed) {
                    // Scale speed based on model size (same formula as in createMaterial)
                    const targetCrossTime = 3.0;
                    const scaledSpeed = (this.maxDistance / targetCrossTime) * config.wavesSpeed;
                    uniforms.uWavesSpeed.value = scaledSpeed;
                }

                if (config.wavesColorIntensity !== undefined && uniforms.uWaveColorIntensity) {
                    uniforms.uWaveColorIntensity.value = config.wavesColorIntensity;
                }

                if (config.wavesMinDistance !== undefined && uniforms.uMinDistance) {
                    // Scale minDistance based on model size (same formula as in createMaterial)
                    const scaledMinDistance = config.wavesMinDistance * this.maxDistance;
                    uniforms.uMinDistance.value = scaledMinDistance;
                }

                if (config.wavesDisplacement !== undefined && uniforms.uDisplacement) {
                    // Scale displacement based on model size (same formula as in createMaterial)
                    const scaledDisplacement = config.wavesDisplacement * this.maxDistance;
                    uniforms.uDisplacement.value = scaledDisplacement;
                }
            }
        });
    }

    public hide(): void {
        if (this.gltf) {
            this.gltf.scene.visible = false;
        }
    }

    /** World-space bbox of the loaded GLB (transform applied). null until load completes. */
    public getWorldBoundingBox(): THREE.Box3 | null {
        if (!this.gltf || this.disposed) {
            return null;
        }
        this.gltf.scene.updateMatrixWorld(true);
        return new THREE.Box3().setFromObject(this.gltf.scene);
    }

    public show(): void {
        if (this.gltf && !this.disposed) {
            this.gltf.scene.visible = true;
        }
    }

    public dispose(): void {
        if (this.disposed) {
            return;
        }
        if (this.gltf) {
            this.gltf.scene.traverse((obj) => {
                // IMPORTANT: PreviewModel content is `THREE.Points`, NOT `THREE.Mesh`.
                // `Points` is a parallel class to `Mesh` in Three.js (both extend Object3D).
                // `obj instanceof THREE.Mesh` alone misses every Points — their materials
                // + textures + compiled WebGL programs leak into the shared renderer's
                // program cache forever, which accumulates across scene transitions and
                // is a primary iOS OOM trigger. `initializeModel` replaces each Points'
                // material with a new ShaderMaterial via `createMaterial`, so each scene
                // contributes 1+ orphan program per transition without this fix.
                if (obj instanceof THREE.Mesh || obj instanceof THREE.Points) {
                    obj.geometry?.dispose();
                    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
                    for (const mat of materials) {
                        if (mat && typeof mat.dispose === 'function') {
                            // Dispose material textures (map, normalMap, roughnessMap, etc.)
                            // before the material itself — Three.js material.dispose()
                            // does NOT cascade to textures, they leak on iOS otherwise.
                            disposeMaterialTextures(mat);
                            mat.dispose();
                        }
                    }
                }
            });
            this.scene.remove(this.gltf.scene);
            this.gltf = null;
        }
        this.gltfLoader.abort();
        this.disposed = true;
    }

    private initializeModel(): void {
        // CLAUDE.md rule #9: GLTFLoader has no abort mechanism — onLoad fires
        // even after dispose(). Without this guard, `this.scene.add(...)` below
        // would crash if the host scene's IIFE-locals were nulled by cleanup
        // (rule #14) before this callback resolves.
        if (this.disposed || !this.gltf) {
            return;
        }
        if (this.transform?.position) {
            this.gltf.scene.position.copy(this.transform.position);
        }
        if (this.transform?.rotation) {
            this.gltf.scene.rotation.copy(this.transform.rotation);
        }
        if (this.transform?.scale) {
            this.gltf.scene.scale.copy(this.transform.scale);
        }

        // Update matrix world before traversing
        this.gltf.scene.updateMatrixWorld(true);

        this.gltf.scene.traverse((child) => {
            if (child instanceof THREE.Points) {
                child.material = this.createMaterial(child.material, child);
            }
        });
        this.scene.add(this.gltf.scene);
    }

    private createMaterial(originalMaterial: THREE.Material, pointsObject: THREE.Points): THREE.ShaderMaterial {
        let pointSize = 'size' in originalMaterial ? (originalMaterial.size as number) : 2;
        pointSize *= this.style.pointSizeFactor;

        // Convert hex color to THREE.Color
        const waveColor = new THREE.Color(this.style.wavesColor);
        const backgroundColor = new THREE.Color(this.style.backgroundColor);

        // Convert displacement axis to number (0=x, 1=y, 2=z)
        const displacementAxisValue = this.style.wavesDisplacementAxis === 'x' ? 0 :
            this.style.wavesDisplacementAxis === 'y' ? 1 : 2;

        // Compute bounding box and center for this specific Points object
        // Use geometry bounding box in local coordinates
        const geometry = pointsObject.geometry;
        geometry.computeBoundingBox();
        const box = geometry.boundingBox;

        let localCenter = new THREE.Vector3(0, 0, 0);
        this.maxDistance = 10.0;

        if (box && !box.isEmpty()) {
            // Get center in local coordinates (relative to the points object)
            localCenter = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());
            this.maxDistance = Math.max(size.x, size.y, size.z) * 0.5;
        }

        // If wavesCenter is specified in config, use it (convert from world to local coordinates)
        if (this.style.wavesCenter) {
            const worldCenter = new THREE.Vector3(
                this.style.wavesCenter.x,
                this.style.wavesCenter.y,
                this.style.wavesCenter.z
            );
            // Transform world -> local using inverse of the object's world matrix
            const inverseMatrix = pointsObject.matrixWorld.clone().invert();
            localCenter = worldCenter.applyMatrix4(inverseMatrix);
        }

        // Auto-scale wave speed based on model size
        // wavesSpeed is a multiplier: 1.0 = model crossed in ~3 seconds
        // This makes it work universally for any model size
        const targetCrossTime = 3.0; // Target time for wave to cross model (seconds) when speed = 1.0
        const scaledWavesSpeed = (this.maxDistance / targetCrossTime) * this.style.wavesSpeed;

        // Auto-scale displacement based on model size
        // wavesDisplacement is percentage of model size (0.01 = 1% of maxDistance)
        // This makes displacement visible and consistent across different model sizes
        const scaledDisplacement = this.style.wavesDisplacement * this.maxDistance;

        // Scale minDistance based on model size (wavesMinDistance is in world units, convert to local)
        const scaledMinDistance = this.style.wavesMinDistance * this.maxDistance;

        const material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                uTime: { value: 0 },
                uCenter: { value: localCenter },
                uPointSize: { value: pointSize },
                uBackgroundColor: { value: backgroundColor },

                uAppearanceDuration: { value: this.style.appearanceDuration },
                uHighPriorityAppearanceRadius: { value: this.style.highPriorityAppearanceRadius },

                uWavesAmplitude: { value: this.style.wavesAmplitude },
                uWavePeriod: { value: this.style.wavesPeriod },
                uWavesSpeed: { value: scaledWavesSpeed },
                uMaxDistance: { value: this.maxDistance },
                uMinDistance: { value: scaledMinDistance },
                uWaveColor: { value: waveColor },
                uWaveColorIntensity: { value: this.style.wavesColorIntensity },
                uDisplacementAxis: { value: displacementAxisValue },
                uDisplacement: { value: scaledDisplacement },

                uRevealTime: { value: 0 },
                uRevealSpeed: { value: 1.0 },
                uRevealOrigin: { value: this.revealOrigin },
                uRevealDistanceScale: { value: this.style.revealDistanceScale },
                uRevealUse3d: { value: this.style.revealUse3d ?? false },
                uColorMultiplier: { value: this.style.colorMultiplier },
                uSizeAttenuation: { value: this.style.sizeAttenuation ? 1.0 : 0.0 },
                uAttenuationScale: { value: this.style.attenuationScale },
            },
            transparent: true,
            depthWrite: true,
            depthTest: true,
            vertexColors: true,
        });

        return material;
    }
}